<template>
  <v-card class="mr-3 ml-3 mb-3 pa-3">
    <v-card-title>
      <span class="headline">Редактирование Модуля ПО</span>
      <v-spacer></v-spacer>
      <v-btn color="warning" text @click="back">Назад</v-btn>
    </v-card-title>
    <v-card-text>
      <v-form v-model="isValid" ref="form">
        <v-row>
          <v-col cols="12">
            <v-text-field
              label="Название ПО"
              v-model="module.name"
              :rules="[notEmptyRule, length50]"
              counter="50"
            ></v-text-field>
          </v-col>

          <v-col cols="12">
            <v-textarea
              label="Доп. инфо"
              :rows="1"
              v-model="module.description"
              :rules="[notEmptyRule]"
              hint="Описание"
              persistent-hint
            ></v-textarea>
          </v-col>

          <v-col cols="12">
            <div class="text-subtitle-2 mb-2">Фичи ПО</div>

            <div class="features-block">
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Название фичи</th>
                      <th class="text-center" style="width: 140px;">По умолчанию</th>
                      <th style="width: 48px;"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="feature in module.features" :key="feature.id">
                      <td>{{ feature.name }}</td>
                      <td class="text-center">
                        <div class="d-flex justify-center">
                          <v-simple-checkbox v-model="feature.defaultEnabled" :ripple="false"></v-simple-checkbox>
                        </div>
                      </td>
                      <td></td>
                    </tr>
                    <tr
                      v-for="(feature, index) in module.newFeatures"
                      :key="'new-' + index"
                      class="unsaved-feature-row"
                    >
                      <td>{{ feature.name }}</td>
                      <td class="text-center">
                        <div class="d-flex justify-center">
                          <v-simple-checkbox v-model="feature.defaultEnabled" :ripple="false"></v-simple-checkbox>
                        </div>
                      </td>
                      <td class="text-center">
                        <v-btn icon x-small @click="removeNewFeature(index)">
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                    <tr v-if="isFeaturesEmpty">
                      <td colspan="3" class="text-center text--secondary">Фич пока нет</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <v-sheet outlined rounded class="add-feature-panel pa-3 mt-3">
                <div class="text-caption font-weight-medium mb-2">Добавить фичу</div>
                <div class="d-flex align-center" style="gap: 12px;">
                  <v-text-field
                    label="Название фичи"
                    v-model="featureDraft.name"
                    :maxlength="50"
                    dense
                    hide-details
                    background-color="white"
                    class="flex-grow-1"
                    style="min-width: 180px;"
                    @keydown.enter.prevent="addFeature"
                  ></v-text-field>
                  <v-checkbox
                    v-model="featureDraft.defaultEnabled"
                    label="По умолчанию"
                    dense
                    hide-details
                    class="mt-0 pt-0 flex-shrink-0"
                  ></v-checkbox>
                  <v-btn color="primary darken-1" text
                         class="flex-shrink-0"
                         @click="addFeature"
                         :disabled="!featureDraft.name.trim()"
                  >Добавить
                  </v-btn>
                </div>
              </v-sheet>

              <div class="text-caption text--secondary mt-2">
                Изменения сохраняются по кнопке «Сохранить». Жёлтым отмечены ещё не сохранённые фичи.
              </div>
            </div>
          </v-col>

          <v-col cols="12">
            <v-switch
              v-model="module.isPrivate"
              label="Модуль для служебного пользования"
              class="mt-0"
            ></v-switch>
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              label="Изменил"
              v-model="module.creatorName"
              disabled
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              label="Дата последнего изменения"
              v-model="formatUpdateTime"
              disabled
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn
        color="primary darken-1"
        text
        @click="updateModuleData"
        :disabled="!isValid"
        >Сохранить
      </v-btn>
    </v-card-actions>

    <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-card>
</template>

<script>
import API from "@/axios/api";

import FormatDateTime from "@/utils/time.js";

export default {
  name: "ModuleEdit",
  props: ["moduleId"],
  data() {
    return {
      loading: true,
      module: {},
      featureDraft: {
        name: "",
        defaultEnabled: false,
      },
      isValid: false,
      notEmptyRule: (v) => !!v || "Поле не может быть пустым",
      length50: (v) => (v && v.length <= 50) || "Максимум 50 символов",
    };
  },
  methods: {
    back() {
      history.back();
    },
    addFeature() {
      const name = (this.featureDraft.name || "").trim();
      if (!name) return;
      if (!Array.isArray(this.module.newFeatures)) {
        this.$set(this.module, "newFeatures", []);
      }
      this.module.newFeatures.push({
        name,
        defaultEnabled: this.featureDraft.defaultEnabled,
      });
      this.featureDraft = { name: "", defaultEnabled: false };
    },
    removeNewFeature(index) {
      this.module.newFeatures.splice(index, 1);
    },
    async getModuleData() {
      await API.get(`modules/${this.moduleId}`)
        .then((response) => {
          console.log(response.data);
          this.module = response.data.module;
          this.$set(this.module, "newFeatures", []);
        })
        .catch((error) => {
          console.log(error);
        });
    },
    updateModuleData() {
      setTimeout(
        () =>
          API.post(`modules/${this.moduleId}`, this.module)
            .then((response) => {
              console.log(response.data);
              this.$store.commit("showSnackbar", {
                color: "success",
                text: "Информация обновлена",
              });
              this.loading = true;
              this.getModuleData();
              this.$store.dispatch("updateModules");
              this.getModuleData().then(() => {
                this.$refs.form.validate();
                this.loading = false;
              });
            })
            .catch((error) => {
              console.log(error);
              this.$store.commit("showSnackbar", {
                color: "error",
                text: "Информация не обновлена",
              });
            }),
        100
      );
    },
  },
  created() {
    this.loading = true;
    this.$store.commit("setCrumbs", [
      { text: "main", disabled: false, to: "/", exact: true },
      { text: "modules", disabled: false, to: "/modules", exact: true },
      { text: this.moduleId, disabled: true },
    ]);
    this.getModuleData().then(() => {
      this.$refs.form.validate();
      this.loading = false;
    });
  },
  computed: {
    formatUpdateTime() {
      if (this.module.updateTime == null) return "";

      return FormatDateTime(this.module.updateTime);
    },
    isFeaturesEmpty() {
      const existing = (this.module.features && this.module.features.length) || 0;
      const added = (this.module.newFeatures && this.module.newFeatures.length) || 0;
      return existing + added === 0;
    },
  },
};
</script>

<style>
.old-chips .v-chip {
  background-color: #bbe9b8 !important;
}

.new-chips .v-chip {
  background-color: #b6d4e7 !important;
}

.v-label--is-disabled {
  color: rgba(0, 0, 0, 0.6) !important;
}

.v-chip--disabled {
  opacity: 1 !important;
}

.unsaved-feature-row td {
  background-color: #fff8e1;
}

.features-block {
  max-width: 680px;
}

.add-feature-panel {
  background-color: #f3f6f3;
}
</style>
